import { Feature, FeatureRenderOptions, MarkerUrls } from '../feature.model';
import { MapDrawingContext } from '../../models/map-drawing-context.model';
import { Document, FieldDataType } from 'src/app/modules/core/store/models/document.model';
import { GoogleMapFeatures } from '../../models/layer-map-features.model';
import { LayerColorsService } from 'src/app/modules/cop/drawing/layer-colors.service';
import { FeatureRenderingUtilityService } from 'src/app/modules/cop/drawing/features/feature-rendering-utility.service';
import { Layer } from 'src/app/modules/cop/store/cop/models/layer.model';
import { Dataset, DatasetType } from 'src/app/modules/core/store/models/dataset.model';
import { cyrb53 } from 'src/app/modules/core/services/hasher';
import { LayerColors } from '../../models/layer-colors.model';
import { GeometryTypes } from '../../models/layer-renderer.model';
import { DatasetZIndexValues } from 'src/app/modules/cop/drawing/models/dataset-zIndex.model';
import { FeatureEvent } from 'src/app/modules/cop/drawing/features/feature.model';
import { MapLabelService } from '../../label.service';
import { v4 as uuidv4 } from 'uuid';
import { MapLayerQueryBuildService } from '../../../services/data/map-layer-query-build.service';
import { MapLayerClusterService } from '../../cluster.service';
import { PopupService } from '../../popups';

export const SELECTED_Z_INDEX = 10000;

export class PowerOutageFeature implements Feature {
  private googleMapFeatures: GoogleMapFeatures;
  private documentHash: number = 0;
  private geometryHash: number = 0;
  private isVisible: boolean = false;
  private isSelected: boolean = false;
  private markerUrls: MarkerUrls = { normal: '', selected: '' };
  private zIndex: number = 0;
  private mouseoutTimers = {};
  private layerUId: string;
  private datasetUId: string;
  private documentUId: string;
  private layerColors: LayerColors;
  private label?: string;
  private minLabelZoomLevel: number;
  private layerHasLabels: boolean;
  private labelService: MapLabelService;
  private clusterService: MapLayerClusterService;
  private markerTimer: string | number | NodeJS.Timeout;
  private polyTimer: string | number | NodeJS.Timeout;
  private popup: PopupService;
  private canShowPopup: boolean;

  constructor() {}

  render(context: MapDrawingContext, options: FeatureRenderOptions): void {
    const { layer, document } = options;

    this.googleMapFeatures = context.mapFeatures[layer.uId][document.uid];

    const dataset = context.mapState.datasetsSet[layer.datasetUId];

    this.layerUId = layer.uId;
    this.datasetUId = dataset.uId;
    this.documentUId = document.uid;

    this.isSelected = !!context.mapState.selectedDocuments[document.uid];
    this.isVisible = context.isDocumentVisible(layer, document);

    this.label = MapLayerQueryBuildService.getDatasetLabelFieldDisplay(document, context.labelFields);
    this.labelService = context.labelService;
    this.clusterService = context.clusterService;
    this.popup = context.popup;
    this.canShowPopup = context.popup?.isEnabledForLayer(layer);
    this.minLabelZoomLevel = this.labelService.getLayerLabelMinZoom(layer);
    this.layerHasLabels = layer.displayOptions?.labelsVisible === true;

    const backgroundColor = this.getBackgroundColor(document);
    this.layerColors = this.getLayerColors(document);
    const size = layer.markerSize ?? 'medium';
    this.markerUrls.normal = `/api/v1/meta/markers/power-outage-${size}/${backgroundColor}/0/0/normal/power.png`;
    this.markerUrls.selected = `/api/v1/meta/markers/power-outage-${size}/${backgroundColor}/0/0/selected/power.png`;
    this.zIndex = this.isSelected ? SELECTED_Z_INDEX : DatasetZIndexValues[dataset.type][dataset.subtype];

    // build and check the document hash...if the values have not changed then
    // bail out on the rendering process.
    const newHash = this.buildDocumentHash(dataset, layer, document, this.isSelected, this.label);
    if (this.documentHash === newHash) {
      return;
    }
    this.documentHash = newHash;

    // check to see if the geometry has changed. If so then we need to remove all existing google map object
    // and redraw. If not then update the colors and icons in the case the workflow has been updated.
    const geoField = FeatureRenderingUtilityService.getGeometryFieldSystemName(dataset, layer);
    const geoJsonValue = document[geoField];
    const newGeoHash = this.buildGeometryHash(geoJsonValue ?? '');
    if (newGeoHash !== this.geometryHash) {
      this.destroy();

      // get the geometry features from the standard geo field in the document
      // and then render each to the map
      this.addGoogleMapObjects(context, layer, dataset, document);

      this.geometryHash = newGeoHash;
    } else {
      // this is an update where the geometry has not changed. just update the geometry colors and marker icons
      this.updateGoogleMapObjects(context, layer, dataset, document);
    }
  }

  setVisible() {
    this.isVisible = true;
    for (let marker of this.googleMapFeatures.markers) {
      marker.setVisible(true);
      this.clusterService.registerMarkerAdd(this.layerUId, marker);
    }
    if (this.layerHasLabels === true) {
      this.showLabel();
    }

    if (this.layerHasLabels === true) {
      this.showLabel();
    }
    for (let poly of this.googleMapFeatures.polygons) {
      poly.setVisible(true);
      const options = poly['__data'].options;
      options.visible = true;
      poly.setOptions(options);
    }
    for (let line of this.googleMapFeatures.polylines) {
      line.setVisible(true);
    }
    for (let circ of this.googleMapFeatures.circles) {
      circ.setVisible(true);
    }
    for (let rect of this.googleMapFeatures.rectangles) {
      rect.setVisible(true);
    }
  }

  setHidden() {
    this.isVisible = false;
    for (let marker of this.googleMapFeatures.markers) {
      marker.setVisible(false);
      const labelOverlay = this.labelService.getMarkerLabel(this.layerUId, marker);
      if (labelOverlay) {
        labelOverlay.setVisible(false);
      }
      this.clusterService.registerMarkerRemove(this.layerUId, marker);
    }
    for (let poly of this.googleMapFeatures.polygons) {
      poly.setVisible(false);
      const options = poly['__data'].options;
      options.visible = false;
      poly.setOptions(options);
    }
    for (let line of this.googleMapFeatures.polylines) {
      line.setVisible(false);
    }
    for (let circ of this.googleMapFeatures.circles) {
      circ.setVisible(false);
    }
    for (let rect of this.googleMapFeatures.rectangles) {
      rect.setVisible(false);
    }

    if (this.markerTimer) {
      clearTimeout(this.markerTimer);
      this.markerTimer = null;
    }

    if (this.polyTimer) {
      clearTimeout(this.polyTimer);
      this.polyTimer = null;
    }
  }

  setSelected() {
    this.isSelected = true;

    this.setMarkerNormal();
    this.setPolygonNormal();
  }

  setUnselected() {
    this.isSelected = false;

    this.setMarkerNormal();
    this.setPolygonNormal();
  }

  showLabel(): void {
    this.layerHasLabels = true;
    if (!!!this.label || this.googleMapFeatures.markers.length === 0) {
      return;
    }

    for (let marker of this.googleMapFeatures.markers) {
      let labelOverlay = this.labelService.getMarkerLabel(this.layerUId, marker);

      if (!labelOverlay) {
        labelOverlay = this.labelService.createLabel(
          marker,
          this.label,
          this.layerUId,
          this.documentUId,
          {
            left: null,
            top: -80
          },
          'map-marker-label'
        );
        this.labelService.addMarkerLabel(this.layerUId, this.minLabelZoomLevel, labelOverlay);
      } else {
        this.labelService.checkMarkerLabelDisplay(this.layerUId, this.minLabelZoomLevel, labelOverlay);
      }
    }
  }

  hideLabel(): void {
    this.layerHasLabels = false;
    for (let marker of this.googleMapFeatures.markers) {
      this.labelService.deleteMarkerLabel(this.layerUId, marker);
    }
  }

  private updateLabel(layer: Layer, marker: any): void {
    const hasLabel = !!this.label && layer.displayOptions?.labelsVisible === true;
    this.layerHasLabels = layer.displayOptions?.labelsVisible === true;

    let labelOverlay = this.labelService.getMarkerLabel(this.layerUId, marker);
    if (hasLabel) {
      if (labelOverlay) {
        labelOverlay.setLabel(this.label);
        this.labelService.checkMarkerLabelDisplay(this.layerUId, this.minLabelZoomLevel, labelOverlay);
      } else {
        this.showLabel();
      }
    } else {
      this.hideLabel();
    }
  }

  destroy(): void {
    for (let circle of this.googleMapFeatures.circles) {
      circle.unbindAll();
      circle.setMap(null);
    }
    this.googleMapFeatures.circles = [];

    for (let marker of this.googleMapFeatures.markers) {
      this.labelService.deleteMarkerLabel(this.layerUId, marker);
      this.clusterService.registerMarkerRemove(this.layerUId, marker);
      marker.unbindAll();
      marker.setMap(null);
    }
    this.googleMapFeatures.markers = [];

    for (let polygon of this.googleMapFeatures.polygons) {
      polygon.unbindAll();
      polygon.setMap(null);
    }
    this.googleMapFeatures.polygons = [];

    for (let polyline of this.googleMapFeatures.polylines) {
      polyline.unbindAll();
      polyline.setMap(null);
    }
    this.googleMapFeatures.polylines = [];

    for (let rectangle of this.googleMapFeatures.rectangles) {
      rectangle.unbindAll();
      rectangle.setMap(null);
    }
    this.googleMapFeatures.rectangles = [];
  }

  addGoogleMapObjects(context: MapDrawingContext, layer: Layer, dataset: Dataset, document: Document) {
    const geoFeatures = FeatureRenderingUtilityService.getGeometryFeaturesForDisplay(dataset, layer, document);
    if (!geoFeatures) {
      return;
    }

    for (let geo of geoFeatures) {
      this.addGoogleMapObject(context, layer, dataset, document, geo);
    }
  }

  addGoogleMapObject(context: MapDrawingContext, layer: Layer, dataset: Dataset, document: Document, geometry: any) {
    if (geometry.type === GeometryTypes.point) {
      this.addPoint(context, layer, dataset, document, geometry);
    } else if (geometry.type === GeometryTypes.polygon || geometry.type === GeometryTypes.multiPolygon) {
      this.addPolygon(context, layer, dataset, document, geometry);
    } else if (geometry.type === GeometryTypes.lineString) {
      this.addPolyline(context, layer, dataset, document, geometry);
    } else if (geometry.type === GeometryTypes.feature) {
      this.addFeature(context, layer, dataset, document, geometry);
    }
  }

  addPoint(context: MapDrawingContext, layer: Layer, dataset: Dataset, document: Document, geometry: any) {
    // account for features that have valid geojson but has empty or invalid coordinates array
    const [lng = null, lat = null] = geometry?.coordinates ?? [];
    
    if (typeof lat !== 'number' || typeof lng !== 'number') {
      return;
    }

    const marker = new google.maps.Marker({
      position: { lat, lng },
      icon: {
        url: !this.isSelected ? this.markerUrls.normal : this.markerUrls.selected
      },
      visible: this.isVisible,
      zIndex: this.zIndex ?? 3000,
      map: context.googleMap
    });

    marker['__data'] = {
      id: uuidv4(),
      layerUId: layer.uId,
      datasetUId: dataset.uId,
      documentUId: document.uid
    };

    const mapEventService = context.mapEvents;

    marker.addListener('mouseover', event => {
      this.onMarkerMouseOver({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: marker
      });

      if (this.canShowPopup) {
        this.popup.open({
          position: marker.getPosition(),
          offsetY: 52,
          layer,
          dataset,
          document,
          offsetBelow: event.domEvent.target.clientHeight
        });
      }
    });

    marker.addListener('mouseout', () => {
      this.onMarkerMouseOut({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: marker
      });

      if (this.canShowPopup) {
        this.popup.close();
      }
    });

    marker.addListener('click', event => {
      mapEventService.onFeatureClick({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: marker
      });

      if (this.canShowPopup) {
        this.popup.close();
      }
    });

    marker.addListener('dblclick', event => {
      mapEventService.onFeatureDoubleClick({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: marker
      });

      if (this.canShowPopup) {
        this.popup.close();
      }
    });

    this.googleMapFeatures.markers.push(marker);
    this.clusterService.registerMarkerAdd(this.layerUId, marker);

    if (layer.displayOptions?.labelsVisible === true) {
      this.showLabel();
    } else {
      this.hideLabel();
    }
  }

  addPolygon(context: MapDrawingContext, layer: Layer, dataset: Dataset, document: Document, geometry: any) {
    const coords = FeatureRenderingUtilityService.normalizePolygonCoordinates(geometry.coordinates);

    const strokeColor = this.layerColors.borderColor;
    const fillColor = this.layerColors.fillColor;
    const strokeWeight = layer.displayOptions.shapeStrokeWidth ?? 1;
    const isCountyLevel = this.isCountyDocument(document);
    const zIndex = isCountyLevel ? 3200 : document.region_type == 'City' ? 3000 : 3100;

    const options = {
      paths: coords,
      strokeColor: strokeColor,
      fillColor: fillColor,
      strokeOpacity: layer.displayOptions.shapeOpacity ?? 1,
      strokeWeight: strokeWeight,
      fillOpacity: !isCountyLevel ? 0.6 : 1.0,
      map: context.googleMap,
      visible: this.isVisible,
      zIndex: zIndex
    };

    const polygon = new google.maps.Polygon(options);

    polygon['__data'] = {
      layerUId: layer.uId,
      datasetUId: dataset.uId,
      documentUId: document.uid,
      options: options,
      strokeColor: strokeColor,
      strokeWeight: strokeWeight,
      fillColor: this.layerColors.fillColor
    };

    const mapEventService = context.mapEvents;

    this.googleMapFeatures.polygons.push(polygon);

    polygon.addListener('mouseover', () => {
      this.onPolygonMouseOver({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: polygon
      });
    });

    polygon.addListener('mouseout', () => {
      this.onPolygonMouseOut({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: polygon
      });
    });

    polygon.addListener('click', event => {
      mapEventService.onFeatureClick({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: polygon
      });
    });

    polygon.addListener('dblclick', event => {
      mapEventService.onFeatureDoubleClick({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: polygon
      });
    });
  }

  addPolyline(context: MapDrawingContext, layer: Layer, dataset: Dataset, document: Document, geometry: any) {
    // account for features that have valid geojson but has empty or invalid coordinates array
    const isValidGeometry = geometry?.coordinates?.every(c => !!c.length && c.every(x => typeof x === 'number')) ?? false;

    if (!isValidGeometry) {
      return;
    }

    const polyline = new google.maps.Polyline({
      path: geometry.coordinates?.map(c => new google.maps.LatLng(c[1], c[0])),
      strokeColor: this.layerColors.borderColor,
      strokeWeight: layer.displayOptions.shapeStrokeWidth ?? 2,
      map: context.googleMap,
      visible: this.isVisible,
      zIndex: this.zIndex ?? 3000
    });

    polyline['__data'] = {
      layerUId: layer.uId,
      datasetUId: dataset.uId,
      documentUId: document.uid
    };

    const mapEventService = context.mapEvents;

    this.googleMapFeatures.polylines.push(polyline);

    polyline.addListener('click', event => {
      mapEventService.onFeatureClick({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: polyline
      });
    });

    polyline.addListener('dblclick', event => {
      mapEventService.onFeatureDoubleClick({
        layerUId: layer.uId,
        datasetUId: dataset.uId,
        documentUId: document.uid,
        feature: polyline
      });
    });
  }

  addFeature(context: MapDrawingContext, layer: Layer, dataset: Dataset, document: Document, geometry: any): void {
    if (geometry.geometry.type === GeometryTypes.point) {
      this.addPoint(context, layer, dataset, document, geometry.geometry);
    } else if (geometry.geometry.type === GeometryTypes.polygon || geometry.geometry.type === GeometryTypes.multiPolygon) {
      this.addPolygon(context, layer, dataset, document, geometry.geometry);
    } else if (geometry.geometry.type === GeometryTypes.lineString) {
      this.addPolyline(context, layer, dataset, document, geometry.geometry);
    } else {
    }
  }

  updateGoogleMapObjects(context: MapDrawingContext, layer: Layer, dataset: Dataset, document: Document): void {
    for (let marker of this.googleMapFeatures.markers) {
      const icon = marker.getIcon() as google.maps.Icon;
      icon.url = this.isSelected ? this.markerUrls.selected : this.markerUrls.normal;
      marker.setIcon(icon);
    }

    if (this.googleMapFeatures.markers.length) {
      this.updateLabel(layer, this.googleMapFeatures.markers[0]);
    }

    for (let polygon of this.googleMapFeatures.polygons) {
      polygon.setValues({
        strokeColor: this.layerColors.borderColor,
        fillColor: this.layerColors.fillColor,
        fillOpacity: layer.displayOptions.shapeOpacity ?? 0.45,
        strokeOpacity: layer.displayOptions.shapeOpacity ?? 1,
        strokeWeight: layer.displayOptions.shapeStrokeWidth ?? 1
      });

      polygon['__data'] = this.getUpdatedPolyOptions(polygon['__data'], layer);
    }

    for (let circle of this.googleMapFeatures.circles) {
      circle.setValues({
        strokeColor: this.layerColors.borderColor,
        fillColor: this.layerColors.fillColor,
        fillOpacity: layer.displayOptions.shapeOpacity ?? 0.45,
        strokeOpacity: layer.displayOptions.shapeOpacity ?? 1,
        strokeWeight: layer.displayOptions.shapeStrokeWidth ?? 0
      });

      circle['__data'] = this.getUpdatedPolyOptions(circle['__data'], layer);
    }

    for (let polyline of this.googleMapFeatures.polylines) {
      polyline.setValues({
        strokeColor: this.layerColors.borderColor,
        fillColor: this.layerColors.fillColor,
        strokeOpacity: layer.displayOptions.shapeOpacity ?? 1,
        strokeWeight: layer.displayOptions.shapeStrokeWidth ?? 0,
        fillOpacity: layer.displayOptions.shapeOpacity ?? 0.45
      });
    }

    for (let rectangle of this.googleMapFeatures.rectangles) {
      rectangle.setValues({
        strokeColor: this.layerColors.borderColor,
        fillColor: this.layerColors.fillColor,
        strokeOpacity: layer.displayOptions.shapeOpacity ?? 1,
        strokeWeight: layer.displayOptions.shapeStrokeWidth ?? 0,
        fillOpacity: layer.displayOptions.shapeOpacity ?? 0.45
      });

      rectangle['__data'] = this.getUpdatedPolyOptions(rectangle['__data'], layer);
    }
  }

  getUpdatedPolyOptions(polyData: any, layer: Layer) {
    return {
      ...polyData,
      options: {
        ...polyData.options,
        strokeColor: this.layerColors.borderColor,
        fillColor: this.layerColors.fillColor,
        strokeOpacity: layer.displayOptions.shapeOpacity ?? 1,
        strokeWeight: layer.displayOptions.shapeStrokeWidth ?? 0,
        fillOpacity: layer.displayOptions.shapeOpacity ?? 0.45
      },
      strokeColor: this.layerColors.borderColor,
      fillColor: this.layerColors.fillColor,
      strokeOpacity: layer.displayOptions.shapeOpacity ?? 1,
      strokeWeight: layer.displayOptions.shapeStrokeWidth ?? 0,
      fillOpacity: layer.displayOptions.shapeOpacity ?? 0.45
    };
  }

  buildMarkerImageUrls(layer: Layer, dataset: Dataset, document: Document, cacheBuster: number): MarkerUrls {
    const badgeCount = this.getBadgeCount(dataset, document) ?? '';
    const backColor = this.layerColors.fillColor.replace('#', '');
    const layerIconUrl = `/api/v1/meta/markers/${layer.uId}_${backColor}_${badgeCount}_${cacheBuster}.png`;
    const layerIconSelectedUrl = `/api/v1/meta/markers/${layer.uId}_${backColor}_${badgeCount}_selected_${cacheBuster}.png`;

    return {
      normal: layerIconUrl,
      selected: layerIconSelectedUrl
    };
  }

  buildDocumentHash(dataset: Dataset, layer: Layer, document: Document, isSelected: boolean, label: string): number {
    const geometryFieldName = FeatureRenderingUtilityService.getGeometryFieldSystemName(dataset, layer);

    const geometry = document[geometryFieldName] ?? '';
    const workflowStepUId = document.workflow_step_uid;
    const fillColor = document.fill_color ?? '';
    const borderColor = document.border_color ?? '';

    const stringToHash = [geometry, workflowStepUId, fillColor, borderColor, isSelected, label ?? ''].join();

    const hash = cyrb53(stringToHash);

    return hash;
  }

  buildGeometryHash(geometry: string): number {
    const hash = cyrb53(geometry);
    return hash;
  }

  getBadgeCount(dataset: Dataset, document: Document): number | null {
    if (dataset.type !== DatasetType.Incident) {
      return null;
    }

    const field = dataset.schema?.fields.find(
      f => f.dataType === FieldDataType.RelatedParent && f.systemName?.startsWith('alerts_') && document[f.systemName] !== undefined
    );
    if (!!!field) {
      return null;
    }

    const value = document[field.systemName];

    return !isNaN(value) ? value : null;
  }

  onMarkerMouseOver(e: FeatureEvent): void {
    if (this.mouseoutTimers[e.documentUId]) {
      clearTimeout(this.mouseoutTimers[e.documentUId]);
      this.mouseoutTimers[e.documentUId] = null;
    }

    this.setMarkerSelected();
    this.startHoverTimer();
  }

  onMarkerMouseOut(e: FeatureEvent): void {
    const that = this;
    const timeout = setTimeout(() => {
      if (!that.isSelected) {
        that.setMarkerNormal();
        clearTimeout(that.markerTimer);
        that.markerTimer = null;
      }
    }, 100);
    this.mouseoutTimers[e.documentUId] = timeout;
  }

  onPolygonMouseOver(e: FeatureEvent): void {
    if (this.mouseoutTimers[e.documentUId]) {
      clearTimeout(this.mouseoutTimers[e.documentUId]);
      this.mouseoutTimers[e.documentUId] = null;
    }

    this.setPolygonSelected();
    this.startPolyHoverTimer();
  }

  onPolygonMouseOut(e: FeatureEvent): void {
    const that = this;
    const timeout = setTimeout(() => {
      if (!that.isSelected) {
        that.setPolygonNormal();
        clearTimeout(this.polyTimer);
        that.polyTimer = null;
      }
    }, 100);
    this.mouseoutTimers[e.documentUId] = timeout;
  }

  setMarkerSelected(): void {
    for (let marker of this.googleMapFeatures.markers) {
      const origZIndex = marker.getZIndex();
      marker['__data'].zIndex = origZIndex;

      marker.setZIndex(SELECTED_Z_INDEX);
      marker.setIcon({
        url: this.markerUrls.selected
      });
    }
  }

  setMarkerNormal(): void {
    for (let marker of this.googleMapFeatures.markers) {
      const zIndex = marker['__data'].zIndex;
      marker.setZIndex(zIndex);

      marker.setIcon({
        url: this.markerUrls.normal
      });
    }
  }

  setPolygonSelected(): void {
    for (let polygon of this.googleMapFeatures.polygons) {
      const options = polygon['__data'].options;
      const origZIndex = options.zIndex;
      polygon['__data'].zIndex = origZIndex;
      options.strokeColor = '#ffffff';
      options.strokeWeight = 4;
      polygon.setOptions(options);
    }
  }

  setPolygonNormal(): void {
    for (let polygon of this.googleMapFeatures.polygons) {
      const options = polygon['__data'].options;
      const strokeColor = polygon['__data'].strokeColor;
      const fillColor = polygon['__data'].fillColor;
      const strokeWeight = polygon['__data'].strokeWeight;
      options.strokeWeight = strokeWeight;
      options.strokeColor = strokeColor;
      options.fillColor = fillColor;
      polygon.setOptions(options);
    }
  }

  getBackgroundColor(document: Document): string {
    if (document.outage_count / document.tracked_count >= 0.6) {
      return 'E03131';
    }

    if (document.outage_count / document.tracked_count >= 0.3) {
      return 'FD7E14';
    }

    if (document.outage_count / document.tracked_count >= 0.1) {
      return 'FCC419';
    }

    return '1864AB';
  }

  getLayerColors(document: Document): LayerColors {
    var color = '#1864AB';

    const stateRegionType = !this.isCountyDocument(document);

    if (stateRegionType) {
      color = this.getStateBackgroundColor(document);
    } else {
      color = this.getCountyBackgroundColor(document);
    }

    return {
      fillColor: color,
      borderColor: LayerColorsService.getLayerBorderColorFromFillColor(color)
    };
  }

  private isCountyDocument(document: Document) {
    return (
      document.region_type === 'County' && document.county_id !== '' && document.county_name !== '' && document.county_name !== 'Unknown'
    );
  }

  getStateBackgroundColor(document: Document): string {
    if (document.outage_count >= 100000) {
      return '#E03131';
    }

    if (document.outage_count >= 50000) {
      return '#FD7E14';
    }

    if (document.outage_count >= 10000) {
      return '#FCC419';
    }

    return '#1864AB';
  }

  getCountyBackgroundColor(document: Document): string {
    if (document.outage_count / document.tracked_count >= 0.6) {
      return '#E03131';
    }

    if (document.outage_count / document.tracked_count >= 0.3) {
      return '#FD7E14';
    }

    if (document.outage_count / document.tracked_count >= 0.1) {
      return '#FCC419';
    }

    return '#1864AB';
  }

  startHoverTimer() {
    if (!!this.markerTimer) {
      clearTimeout(this.markerTimer);
      this.markerTimer = null;
    }

    const that = this;
    this.markerTimer = setTimeout(() => {
      if (!that.isSelected) {
        that.setMarkerNormal();
      }
    }, 3000);
  }

  startPolyHoverTimer() {
    if (!!this.polyTimer) {
      clearTimeout(this.polyTimer);
      this.polyTimer = null;
    }

    const that = this;
    this.polyTimer = setTimeout(() => {
      if (!that.isSelected) {
        that.setPolygonNormal();
      }
    }, 3000);
  }
}
